import { useState, useRef, useEffect } from 'react';
import type {
  DataType,
  ProposeTableFillInput,
  TableInputDefinition,
} from '@arcanum-hq/shared-types';
import { Button } from '@/components/ui/Button';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import { useTableInputsEdit } from '@/hooks/use-table-inputs-edit';
import {
  hydrateTableGrid,
  buildWritesFromGrid,
  buildDisplayRows,
} from './hydrate-table-grid';
import { formatCellDisplay } from './TableGridEditor';
import { PhaseShell } from './ToolResultRendererSkeleton';

/**
 * `<TableFillProposal>` — the `propose_table_fill` card.
 *
 * ## Auto-apply model (table-input-ux, 2026-06-08 — user directive):
 *
 * "The deal is set up FROM the proposal." When the agent proposes a table, the
 * proposed values are written to the model AUTOMATICALLY (units AND rents) — the
 * user does not have to click anything. Concretely:
 *
 *   - On render, if the proposal's values differ from the current model, the
 *     card fires ONE `POST /table-edit` write of the full proposed grid (which
 *     appends a `[table-edit:*]` receipt at the end of the thread).
 *   - The card itself renders READ-ONLY/frozen to its proposal-at-turn (the
 *     immutable-transcript contract) — Apply/Dismiss are gone (they'd be no-ops
 *     since the values are already set).
 *   - Re-editing happens via the receipt's "Edit" (a fresh `<TableGridEditor>`
 *     at the bottom of the thread → `/table-edit` → new receipt).
 *
 * ## Value-based "applied" detection (NOT source-based):
 * "applied" = every editable cell the proposal sets already equals the current
 * model value. Source-based detection (cell is `source:user`) was wrong for a
 * REPEAT proposal to the same table — the named ranges are already `user` from a
 * prior apply, so the new proposal would be wrongly treated as applied and never
 * written. Value-comparison fires correctly for the first proposal, scaled
 * repeats, and never re-fires on reload (the model already matches).
 *
 * ## Legacy fallback (D2): when there is no `conversationId`/`tableDef` (e.g.
 * read-only share), the old synthesized-turn confirm path is preserved.
 */

interface TableFillProposalProps {
  state: ToolCallState;
  /** @deprecated unused — resolved via `tableDef` now. Kept for call-site shape. */
  modelId?: string | null;
  /** Conversation ID — enables the auto-apply direct-write path. */
  conversationId?: string | null;
  /**
   * Current input provenance from `useConversationViews().inputs`. Supplies the
   * current model values that drive the value-based applied detection + the
   * schema-default fallback. `undefined` while `/views` is still loading.
   */
  currentInputs?:
    | Record<string, { value: unknown; source?: string }>
    | undefined;
  /**
   * Full `TableInputDefinition` for the table named in the proposal. Passed
   * from `ToolResultRenderer`. When absent the card falls back to the legacy
   * synthesized-turn path (D2 back-compat).
   */
  tableDef?: TableInputDefinition | null;
  /**
   * Whether THIS proposal has already been applied — computed by `<ChatThread>`
   * as "a `[table-edit:<table>]` receipt exists LATER in the thread than this
   * proposal." This is the durable, per-proposal applied signal: it survives
   * remounts/reloads (the receipt is persisted), works for conversations
   * applied before any client flag existed, and — unlike a source-based "table
   * touched" check — still lets a genuinely NEW re-proposal auto-apply (it has
   * no receipt after it yet). When true, the card freezes and never auto-fires.
   * (user feedback 2026-06-09.)
   */
  alreadyApplied?: boolean;
  /**
   * `true` while the agent run that produced this proposal is STILL streaming
   * (the hosting message's `isStreaming`). GATES the auto-apply effect so the
   * direct-write `POST /table-edit` fires only AFTER the turn finishes
   * (table-fill-auto-apply-checkpoint-race fix, 2026-06-11).
   *
   * Why this is load-bearing: the apply path's server-side `appendSystemMessage`
   * writes a hand-fabricated LangGraph checkpoint whose `messages`-channel
   * version is `prevVersion + 1`. Fired mid-run, that version collides with the
   * version the still-running agent assigns to its next super-step (the final
   * summary message); PostgresSaver's blob store de-dupes on
   * `(thread, ns, channel, version)`, so whichever write lands first wins and
   * the agent's final-summary blob is silently dropped — the summary streams
   * live then vanishes on stream-end/reload. Deferring the apply to post-`done`
   * means `appendSystemMessage` reads the settled checkpoint (summary already
   * persisted) and chains its breadcrumb on top at the next version — both
   * survive. Default `false` so hydrated/persisted cards (never streaming) and
   * legacy callers auto-apply immediately, unchanged.
   */
  runStreaming?: boolean;
  /**
   * Synthesized-turn callback — kept for back-compat with the system.xml
   * prefix-recognition rule (D2). Used ONLY by the legacy fallback path.
   */
  onSynthesizedTurn?: (message: string) => void;
  /** @deprecated alias for `onSynthesizedTurn`. */
  onConfirm?: (synthesizedUserMessage: string) => void;
}

type CellValue = number | string | boolean;

export function TableFillProposal({
  state,
  conversationId,
  currentInputs,
  tableDef,
  alreadyApplied = false,
  runStreaming = false,
  onSynthesizedTurn,
  onConfirm,
}: TableFillProposalProps) {
  const [dismissed, setDismissed] = useState(false);
  // appliedThisSession: set on a successful auto-apply in THIS mount so the card
  // freezes immediately (before the `/views` refetch lands).
  const [appliedThisSession, setAppliedThisSession] = useState(false);
  const [autoApplyError, setAutoApplyError] = useState<string | null>(null);
  // Bumped by the Retry affordance to re-run the auto-apply effect after a
  // failure (the effect can't re-fire on its own — its deps don't change).
  const [retryNonce, setRetryNonce] = useState(0);
  const autoFiredRef = useRef(false);
  const tableInputsEdit = useTableInputsEdit();

  const args = readArgs(state);

  // ── Hydration (null when no tableDef or no args) ────────────────────────────
  const grid =
    args && tableDef
      ? hydrateTableGrid(
          tableDef,
          currentInputs,
          args.cells as Record<
            string,
            Record<string, number | string | boolean>
          >
        )
      : null;

  // ── Value-based applied detection ───────────────────────────────────────────
  // Only editable cells the proposal actually sets (overlayValue defined) count.
  const overlayEditableCells = grid
    ? grid.rows.flatMap((r) =>
        r.cells.filter((c) => c.editable && c.overlayValue !== undefined)
      )
    : [];
  const proposalApplied =
    overlayEditableCells.length > 0 &&
    overlayEditableCells.every(
      (c) => Number(c.currentValue) === Number(c.overlayValue)
    );

  // DURABLE re-fire guard (user feedback 2026-06-09): `alreadyApplied` (a
  // `[table-edit:*]` receipt exists LATER in the thread than this proposal,
  // computed by <ChatThread>) means this exact proposal was already applied —
  // freeze + never auto-fire again, even after the user edits the table or
  // remounts the card (Model-tab round-trip). A genuinely NEW re-proposal has
  // no receipt after it yet → `alreadyApplied` is false → it still auto-applies
  // once. The value-based `proposalApplied` is a secondary short-circuit for the
  // first-paint-before-receipt case.
  const frozen = proposalApplied || appliedThisSession || alreadyApplied;

  // ── Auto-apply effect (writes the proposal once when it differs from model) ──
  // Gated on `currentInputs !== undefined` so we never fire against default
  // values before /views loads (which would re-apply on every reload).
  const canAutoApply =
    !!grid &&
    !!conversationId &&
    currentInputs !== undefined &&
    !frozen &&
    !dismissed &&
    // Defer until the agent turn finishes — auto-applying mid-run collides the
    // /table-edit checkpoint write with the agent's pending final-message
    // super-step and the final summary is silently dropped (see `runStreaming`
    // JSDoc; table-fill-auto-apply-checkpoint-race fix, 2026-06-11).
    !runStreaming &&
    state.phase !== 'input_streaming' &&
    state.phase !== 'aborted';

  useEffect(() => {
    // Re-fire guard: if this proposal already produced a receipt later in the
    // thread (`alreadyApplied`) OR the model already matches it, mark fired and
    // never auto-apply again. The receipt-based signal is durable across
    // remounts/reloads and distinguishes "this proposal re-firing" (frozen)
    // from "a new re-proposal" (no receipt yet → applies).
    if (proposalApplied || appliedThisSession || alreadyApplied) {
      autoFiredRef.current = true;
      return;
    }
    if (
      !canAutoApply ||
      autoFiredRef.current ||
      !grid ||
      !conversationId ||
      !args
    )
      return;
    autoFiredRef.current = true;
    setAutoApplyError(null);
    tableInputsEdit.mutate(
      {
        conversationId,
        table: args.table,
        writes: buildWritesFromGrid(grid.rows, {}),
        displayRows: buildDisplayRows(grid.rows, {}),
      },
      {
        onSuccess: () => setAppliedThisSession(true),
        onError: (err) => {
          // Keep autoFiredRef set so the effect doesn't loop; recovery is the
          // explicit Retry affordance (bumps retryNonce → effect re-runs).
          setAutoApplyError(
            err.message ?? 'Failed to set table values. Please retry.'
          );
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canAutoApply,
    proposalApplied,
    appliedThisSession,
    alreadyApplied,
    retryNonce,
  ]);

  // ── Early-exit states ──────────────────────────────────────────────────────

  if (dismissed) {
    return (
      <p
        className="text-xs font-mono italic text-text-dim"
        data-testid="table-fill-dismissed"
      >
        Proposal dismissed.
      </p>
    );
  }

  if (state.phase === 'input_streaming' || !args) {
    return <PhaseShell variant="skeleton" title="TABLE FILL" />;
  }

  if (state.phase === 'aborted') {
    return (
      <PhaseShell variant="aborted" title={`TABLE FILL — ${args.table}`} />
    );
  }

  const tableDisplayName = tableDef?.displayName ?? args.table;

  // ── Auto-apply path: render the frozen proposal-at-turn (read-only) ─────────
  if (grid && conversationId) {
    const applying = tableInputsEdit.isPending && !frozen;

    // Once a receipt represents this proposal (`alreadyApplied`), collapse the
    // card to a one-line marker — the editable receipt below IS the table, so a
    // full duplicate grid here is noise. The proposal stays as a lightweight
    // "the agent suggested this here" history marker. (user feedback 2026-06-09.)
    if (alreadyApplied) {
      return (
        <div
          data-testid="table-fill-proposal"
          data-phase={state.phase}
          data-table={args.table}
          data-frozen="true"
          data-collapsed="true"
          className="flex items-center gap-2 text-micro font-mono"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-accent/70 shrink-0" />
          <span className="uppercase tracking-[1.2px] font-semibold text-text-muted">
            Suggested &middot; {tableDisplayName}
          </span>
          <span className="text-text-dim normal-case tracking-normal font-normal">
            — applied; see the table below
          </span>
        </div>
      );
    }

    return (
      <div
        data-testid="table-fill-proposal"
        data-phase={state.phase}
        data-table={args.table}
        data-frozen={frozen ? 'true' : 'false'}
        className="space-y-2"
      >
        <div className="flex items-center justify-between gap-2">
          <p className="text-micro font-mono font-semibold tracking-[1.2px] uppercase text-text-muted">
            Suggested &middot; {tableDisplayName}
          </p>
          {applying ? (
            <span
              className="text-micro font-mono text-text-muted"
              data-testid="table-fill-applying-tag"
            >
              setting up…
            </span>
          ) : (
            <span
              className="flex items-center gap-1.5 text-micro font-mono text-accent/80"
              data-testid="table-fill-applied-tag"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
              applied
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs font-mono tabular-nums">
            <thead>
              <tr className="border-b border-panel-border">
                <th className="px-2 py-1.5 text-left text-text-muted font-semibold uppercase tracking-[0.5px]" />
                {tableDef!.columns.map((c) => (
                  <th
                    key={c.key}
                    className="px-2 py-1.5 text-left text-text-muted font-semibold uppercase tracking-[0.5px]"
                  >
                    {c.displayName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row) => (
                <tr
                  key={row.rowLabel}
                  className="border-b border-panel-border/50"
                >
                  <td className="px-2 py-1.5 text-text-bright font-semibold">
                    {row.rowLabel}
                  </td>
                  {row.cells.map((cell) => (
                    <td
                      key={cell.colKey}
                      className="px-2 py-1.5 text-text-muted"
                      data-testid={`cell-${row.rowLabel}-${cell.colKey}`}
                    >
                      {cell.displayValue !== undefined
                        ? formatCellDisplay(
                            cell.displayValue,
                            cell.dataType as DataType
                          )
                        : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {autoApplyError ? (
          <div className="flex items-center gap-3">
            <p
              className="text-xs font-mono text-danger"
              data-testid="table-fill-error"
            >
              {autoApplyError}
            </p>
            <button
              type="button"
              data-testid="table-fill-retry"
              onClick={() => {
                autoFiredRef.current = false;
                setAutoApplyError(null);
                setRetryNonce((n) => n + 1);
              }}
              className="text-xs font-mono text-accent/80 hover:text-accent transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <p className="text-micro font-mono text-text-dim">
            {applying
              ? 'Applying these values to your model…'
              : 'Edit from the receipt below to revise.'}
          </p>
        )}
      </div>
    );
  }

  // ── Legacy fallback (D2): no schema OR no conversationId ────────────────────
  return (
    <LegacyTableFillProposal
      args={args}
      tableDisplayName={tableDisplayName}
      onConfirm={onConfirm ?? onSynthesizedTurn ?? (() => undefined)}
      onDismiss={() => setDismissed(true)}
    />
  );
}

// ── Legacy synthesized-turn fallback ─────────────────────────────────────────────

function LegacyTableFillProposal({
  args,
  tableDisplayName,
  onConfirm,
  onDismiss,
}: {
  args: ProposeTableFillInput;
  tableDisplayName: string;
  onConfirm: (message: string) => void;
  onDismiss: () => void;
}) {
  function handleApply() {
    const payload = {
      table: args.table,
      rows: args.rowLabels.map((label) => ({
        label,
        values: (args.cells[label] as Record<string, CellValue>) ?? {},
      })),
    };
    onConfirm(
      `Confirmed table fill for ${args.table}: ${JSON.stringify(payload)}`
    );
  }

  return (
    <div
      data-testid="table-fill-proposal"
      data-table={args.table}
      className="space-y-2"
    >
      <div className="space-y-1">
        <p className="text-micro font-mono font-semibold tracking-[1.2px] uppercase text-text-muted">
          Suggested &middot; {tableDisplayName}
        </p>
        <p className="text-xs font-mono text-text-bright/80">
          review and confirm to apply
        </p>
        {args.rationale && (
          <p className="text-sm text-text-primary">
            <span className="text-text-muted">Basis: </span>
            {args.rationale}
          </p>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs font-mono tabular-nums">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="px-2 py-1.5 text-left text-text-muted font-semibold uppercase tracking-[0.5px]" />
              {args.columns.map((c) => {
                const col = c as { key: string; label: string };
                return (
                  <th
                    key={col.key}
                    className="px-2 py-1.5 text-left text-text-muted font-semibold uppercase tracking-[0.5px]"
                  >
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {args.rowLabels.map((rowLabel) => (
              <tr key={rowLabel} className="border-b border-panel-border/50">
                <td className="px-2 py-1.5 text-text-bright font-semibold">
                  {rowLabel}
                </td>
                {args.columns.map((c) => {
                  const col = c as {
                    key: string;
                    label: string;
                    dataType: DataType;
                  };
                  const seedValue = (
                    args.cells[rowLabel] as Record<string, CellValue>
                  )?.[col.key];
                  return (
                    <td
                      key={col.key}
                      className="px-2 py-1.5 text-text-muted"
                      data-testid={`cell-${rowLabel}-${col.key}`}
                    >
                      {formatCellDisplay(seedValue, col.dataType)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          variant="primary"
          onClick={handleApply}
          data-testid="table-fill-confirm"
        >
          Apply to model
        </Button>
        <Button
          variant="ghost"
          onClick={onDismiss}
          data-testid="table-fill-cancel"
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}

// ── Utility ───────────────────────────────────────────────────────────────────────

/**
 * Defensive narrow on `state.input` — Phase 1's reducer settles parsed
 * tool_call args onto `state.input` at the `tool_input_ready` transition; the
 * hydration path (deriveToolCallStates) restores it from the persisted
 * tool-call args on reload (table-input-ux P1, 2026-06-08).
 */
function readArgs(state: ToolCallState): ProposeTableFillInput | null {
  if (state.phase === 'input_streaming') return null;
  const input = state.input;
  if (input === null || typeof input !== 'object') return null;
  const candidate = input as Partial<ProposeTableFillInput>;
  if (typeof candidate.table !== 'string' || candidate.table.length === 0)
    return null;
  if (!Array.isArray(candidate.rowLabels) || candidate.rowLabels.length === 0)
    return null;
  if (!Array.isArray(candidate.columns) || candidate.columns.length === 0)
    return null;
  if (candidate.cells === null || typeof candidate.cells !== 'object')
    return null;
  if (
    typeof candidate.rationale !== 'string' ||
    candidate.rationale.length === 0
  )
    return null;
  if (
    typeof candidate.defaultsBasis !== 'string' ||
    candidate.defaultsBasis.length === 0
  ) {
    return null;
  }
  return candidate as ProposeTableFillInput;
}
